import React, { useEffect, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Clock, KeyRound, Play, Square } from "lucide-react";
import Seo from "../components/Seo";
import { toast } from "sonner";
import ChatPanel from "../components/ChatPanel";

export default function SessionLive() {
  const { id } = useParams();
  const { user } = useAuth();
  const [b, setB] = useState(null);
  const [otp, setOtp] = useState("");
  const [generated, setGenerated] = useState("");
  const [elapsed, setElapsed] = useState(0);
  const [overtimeMin, setOvertimeMin] = useState(0);

  const load = useCallback(
    () => api.get(`/bookings/${id}`).then(r => setB(r.data)),
    [id]
  );

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!b?.session_started_at || b.session_ended_at) return;
    const start = new Date(b.session_started_at).getTime();
    const tick = () => {
      const sec = Math.floor((Date.now() - start) / 1000);
      setElapsed(sec);
      const allowedSec = (b.hours || 1) * 3600;
      setOvertimeMin(Math.max(0, Math.floor((sec - allowedSec) / 60)));
    };
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, [b]);

  const gen = async () => {
    try {
      const { data } = await api.post(`/bookings/${id}/generate-otp`);
      setGenerated(data.otp);
      toast.success("OTP generated. Share with trainer.");
    } catch (e) { toast.error(e.response?.data?.detail || "Cannot generate OTP"); }
  };

  const verify = async () => {
    try {
      await api.post(`/bookings/${id}/verify-otp`, { otp });
      toast.success("Session started");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Invalid OTP"); }
  };

  const endSession = async () => {
    try {
      const { data } = await api.post(`/bookings/${id}/end-session`, { overtime_minutes: overtimeMin });
      if (data.overtime_minutes > 0) {
        toast.success(`Session ended. ${data.overtime_minutes} min overtime ($${data.overtime_amount}) sent to the learner for approval.`);
        if (data.overtime_minutes < data.overtime_minutes_claimed) {
          toast.info(`Overtime capped at ${data.overtime_minutes} min — that's the actual time measured beyond the booked duration.`);
        }
      } else {
        toast.success("Session ended.");
      }
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Cannot end session"); }
  };

  if (!b) return <div className="max-w-3xl mx-auto p-12 text-muted-foreground">Loading…</div>;

  const isLearner = user?.user_id === b.learner_id;
  const isTrainer = user?.user_id === b.trainer_id;
  const fmt = (s) => `${String(Math.floor(s/3600)).padStart(2,"0")}:${String(Math.floor((s%3600)/60)).padStart(2,"0")}:${String(s%60).padStart(2,"0")}`;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12 space-y-8" data-testid="session-live">
      <Seo title="Live Session – DriveTerrain" description="Your live driving session." canonical="/session" robots="noindex, nofollow" hrefLangs={false} />
      <div>
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-primary">Live Session</div>
        <h1 className="text-3xl md:text-4xl font-display font-semibold tracking-tight mt-1">Booking #{b.booking_id.slice(-6)}</h1>
        <p className="text-muted-foreground mt-1">Status: <span className="text-foreground font-medium" data-testid="session-status">{b.status}</span></p>
      </div>

      {/* Pre-start panel */}
      {b.payment_status === "paid" && !b.session_started_at && (
        <div className="border border-border bg-card p-6 grid md:grid-cols-2 gap-8">
          {isLearner && (
            <div>
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground"><KeyRound className="w-4 h-4 text-primary" /> Your OTP</div>
              <div className="font-display text-4xl font-semibold tracking-widest mt-2" data-testid="generated-otp">{generated || "------"}</div>
              <Button className="rounded-sm mt-4" onClick={gen} data-testid="gen-otp-btn">Generate OTP</Button>
              <p className="text-xs text-muted-foreground mt-3">Share this 6-digit code with your trainer to start the session.</p>
            </div>
          )}
          {isTrainer && (
            <div>
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground"><Play className="w-4 h-4 text-primary" /> Verify OTP to start</div>
              <Label className="mt-3 block">Enter learner's OTP</Label>
              <Input value={otp} onChange={e => setOtp(e.target.value)} maxLength={6} className="rounded-sm tracking-widest text-center text-lg mt-1" data-testid="trainer-otp-input" />
              <Button className="rounded-sm mt-4" onClick={verify} data-testid="start-session-btn">Start session</Button>
            </div>
          )}
        </div>
      )}

      {/* Timer */}
      {b.session_started_at && (
        <div className="border border-border bg-card p-6">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground"><Clock className="w-4 h-4 text-primary" /> Session timer</div>
          <div className="font-display text-6xl font-semibold mt-2 tracking-tight" data-testid="timer-display">{fmt(elapsed)}</div>
          <div className="mt-2 text-sm">
            Allowed: {b.hours}h ·{" "}
            <span className={overtimeMin > 0 ? "text-primary font-semibold" : "text-muted-foreground"} data-testid="overtime-min">
              Overtime: {overtimeMin} min
            </span>
          </div>
          {isTrainer && !b.session_ended_at && (
            <Button className="rounded-sm mt-4" onClick={endSession} data-testid="end-session-btn"><Square className="w-4 h-4 mr-2" /> End session</Button>
          )}
          {b.session_ended_at && (
            <div className="mt-4 text-sm space-y-1">
              <div>Ended: {new Date(b.session_ended_at).toLocaleString()}</div>
              <div>Overtime: <span className="font-semibold" data-testid="overtime-amount">${b.overtime_amount?.toFixed?.(2) || 0}</span></div>
              {b.overtime_status === "pending_learner_approval" && (
                <div className="text-xs text-muted-foreground" data-testid="overtime-pending-note">
                  Awaiting the learner's approval and payment — overtime is not payable until then.
                </div>
              )}
              {b.overtime_status === "declined" && (
                <div className="text-xs text-destructive" data-testid="overtime-declined-note">
                  The learner declined this overtime.
                </div>
              )}
              {b.overtime_status === "settled" && (
                <div className="text-xs text-primary" data-testid="overtime-settled-note">
                  Approved and paid — included in your payable balance.
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {b.payment_status !== "paid" && (
        <div className="border border-border bg-card p-6 text-sm">
          This booking is unpaid. <Link to={`/booking/${b.booking_id}`} className="text-primary hover:underline">Complete payment</Link> to proceed.
        </div>
      )}

      <div className="pt-2">
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground mb-3">In-session chat</div>
        <ChatPanel bookingId={b.booking_id} otherName={isLearner ? "Your trainer" : "Your learner"} />
      </div>
    </div>
  );
}
